import { usePostHog } from "@-/com.npmjs.posthog-react-native";
import { AppState } from "@-/com.npmjs.react-native";
import type { AuthSessionSnapshot } from "@-/dev.improve.haniverse.lib.auth.client";
import {
  captureAnalyticsEvent,
  consumeAuthMethodHint,
  publishAnalyticsDistinctId,
} from "@-/dev.improve.haniverse.lib.analytics";
import { createBoundedSerialQueue, withDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { setSentryAnalyticsIdentity } from "@-/dev.improve.haniverse.lib.sentry";
import {
  PostHogFeatureFlagClientContext,
  PostHogIdentityReadyContext,
  PostHogTrackingReadyContext,
} from "#feature-flags";
import { useEffect, useRef, useState, type PropsWithChildren } from "react";

/**
 * PostHog's `ready()` (a local storage hydration) and one feature-flag reload (one network
 * request) each finish in well under a second normally. Each step is bounded so a flag request that
 * never answers cannot hold the identity queue — and every later identity change behind it — open
 * forever; the refresh then fails like any other flag outage (flags unavailable, warning logged).
 */
const POSTHOG_STEP_DEADLINE_MS = 15_000;

/** One refresh is three bounded steps plus synchronous identity calls. */
const IDENTITY_REFRESH_RUN_DEADLINE_MS = 3 * POSTHOG_STEP_DEADLINE_MS + 5000;

/** Refreshes queue behind at most a few others (identity changes, app foregrounding). */
const IDENTITY_REFRESH_WAIT_DEADLINE_MS = 3 * IDENTITY_REFRESH_RUN_DEADLINE_MS;

/** An account created this recently was signed UP by this sign-in, not signed back in. */
const NEW_ACCOUNT_WINDOW_MS = 10 * 60 * 1000;

/** Serialize identity changes so an old flag request cannot grant the next account access. */
export function PostHogIdentity({
  children,
  session,
}: PropsWithChildren<{ session: AuthSessionSnapshot }>) {
  const posthog = usePostHog();
  const userId = session.user?.id ?? null;
  const email = session.user?.email;
  // A number, not the Date: a fresh Date object per render would re-run the identity effect.
  const createdAtMs =
    session.user?.createdAt === undefined ? null : new Date(session.user.createdAt).getTime();
  const identityKey = session.isResolved ? (userId ?? "signed-out") : "unresolved";
  const [readyIdentity, setReadyIdentity] = useState<string | null>(null);
  const [trackingIdentity, setTrackingIdentity] = useState<string | null>(null);
  // The identity this runtime last settled, to tell a real sign-in (signed-out → user) from a
  // cold start that resolves straight to a stored session.
  const settledIdentityRef = useRef<string | null>(null);
  // Serializes identity refreshes. `advance` on overrun: a refresh re-checks `cancelled` after
  // every await, so one that outlived its deadline can no longer change the identity.
  const [identityQueue] = useState(() =>
    createBoundedSerialQueue({
      name: "PostHog identity",
      onOverrun: "advance",
      runDeadline: IDENTITY_REFRESH_RUN_DEADLINE_MS,
      waitDeadline: IDENTITY_REFRESH_WAIT_DEADLINE_MS,
    }),
  );

  useEffect(() => {
    let cancelled = false;
    setReadyIdentity(null);
    setTrackingIdentity(null);
    const refresh = () => {
      void identityQueue
        .run("refresh", async () => {
          if (cancelled || !session.isResolved) {
            return;
          }
          await withDeadline(
            { deadline: POSTHOG_STEP_DEADLINE_MS, operation: "PostHog ready" },
            async () => posthog.ready(),
          );
          // Drain any SDK-triggered request before reset/identify changes its identity. A flag
          // outage must not leave analytics on the previous identity, so a failed drain only logs.
          try {
            await withDeadline(
              { deadline: POSTHOG_STEP_DEADLINE_MS, operation: "PostHog feature flag reload" },
              async () => posthog.reloadFeatureFlagsAsync(),
            );
          } catch (error) {
            logger(import.meta.location).warn("PostHog flag drain before identify failed", error);
          }
          if (cancelled) {
            return;
          }
          if (
            posthog.getDistinctId() !== userId &&
            posthog.getDistinctId() !== posthog.getAnonymousId()
          ) {
            posthog.reset();
          }
          if (userId !== null) {
            posthog.identify(userId, email ? { email } : {});
          }
          const previousIdentity = settledIdentityRef.current;
          settledIdentityRef.current = identityKey;
          if (userId !== null && previousIdentity === "signed-out") {
            captureAnalyticsEvent("account_authenticated", {
              is_new_account: createdAtMs !== null && Date.now() - createdAtMs < NEW_ACCOUNT_WINDOW_MS,
              method: consumeAuthMethodHint() ?? "unknown",
            });
          }
          const distinctId = posthog.getDistinctId();
          publishAnalyticsDistinctId(distinctId);
          setSentryAnalyticsIdentity({ distinctId, getSessionId: () => posthog.getSessionId() });
          setTrackingIdentity(identityKey);
          const flags = await withDeadline(
            { deadline: POSTHOG_STEP_DEADLINE_MS, operation: "PostHog feature flag reload" },
            async () => posthog.reloadFeatureFlagsAsync(),
          );
          if (!cancelled) {
            setReadyIdentity(flags === undefined ? null : identityKey);
          }
        })
        .catch((error: unknown) => {
          if (!cancelled) {
            setReadyIdentity(null);
          }
          logger(import.meta.location).warn("PostHog feature flags unavailable", error);
        });
    };
    refresh();
    const subscription = AppState.addEventListener("change", (state) => {
      if (state === "active") {
        refresh();
      }
    });
    return () => {
      cancelled = true;
      subscription.remove();
    };
  }, [createdAtMs, email, identityKey, identityQueue, posthog, session.isResolved, userId]);

  return (
    <PostHogIdentityReadyContext value={readyIdentity === identityKey}>
      <PostHogTrackingReadyContext value={trackingIdentity === identityKey}>
        <PostHogFeatureFlagClientContext value={posthog}>
          {children}
        </PostHogFeatureFlagClientContext>
      </PostHogTrackingReadyContext>
    </PostHogIdentityReadyContext>
  );
}
